'use strict';

import Helpers from './Helpers';
import ArgValidator from 'bam-arg-validator';
import BamTeamData from 'bam-team-data';
import BamButton from './BamButton';
import BamButtonGroup from './BamButtonGroup';
const cloneDeep = BamTeamData.getHelpers().cloneDeep;

/*
 * See constructor JSDocs for details about each option.  Note that some required options also have a default value.
 * This is because, for example, the default value for appView, 'web', is treated differently from any other value for
 * appView (e.g., 'app').
 */

const SUPPORTED_OPTION_SPECS = {
    bamLinker: {
        expectedType: 'object',
        required: true
    },
    dictionaryData: {
        expectedType: 'object',
        required: true
    },
    callerCode: {
        expectedType: 'string',
        required: true
    }
 };

const defaultOptions = Helpers.getDefaultOptions(SUPPORTED_OPTION_SPECS);

/**
 * Property-agnostic base class for property-specific game-util classes such as MlbGameUtils which operate
 * on data from the Sports Data API ("statsapi").
 *
 * _Nothing_ property-specific belongs in this base class.  Methods may provide default behavior if that's possible,
 * or throw an Error by calling Helpers.throwNotImplementedError().
 */
class BamGameUtils {

    /**
     *
     * @param options Options supplied by the subclass constructor; unlikely to be user-specified; more likely to make
     *   the subclass suitable for its concrete use
     * @constructor
     */
    constructor(options) {
        Helpers.validateOptions(options, SUPPORTED_OPTION_SPECS); // throw error if options not valid
        const opts = Object.assign(cloneDeep(defaultOptions), options || {}); // now we have default vals for missing options
        this._bamLinker = opts.bamLinker;
        this._dictionaryData = opts.dictionaryData;
        this._callerCode = opts.callerCode;
    }

    /**
     * Returns all of the buttons (as BamButton subclass instances) which may be displayed for this game, depending
     * on whether the game is ongoing, future, or past; whether there's media available or not; etc.
     * @param {object} game A statsapi game node
     * @returns {object} a JS object with keys such as 'recap', 'watch', etc; values are BamButton subclasses
     * @throws Error if called.  Subclasses must implement this method.
     */
    getButtons(game) {
        Helpers.throwNotImplementedError('getButtons');
    }

    /**
     * Returns 'past', 'present', or 'future' depending on game status; this is highly property-dependent; thus this
     * implementation throws a not-implemented Error
     * @param game
     * @returns  'past', 'present', or 'future'
     * @throws Error if called.  Subclasses must implement this method.

     */
    getGameTense(game) {
        Helpers.throwNotImplementedError('getGameTense');
    }

    /**
     * Return The BamLinker subclass instance which backs this BamGameUtils instance.  If a consumer is using (for
     * example) both an MlbLinker instance and an MlbGameUtils instance _on the client side_, the consumer should
     * instantiate the MlbGameUtils instance only, and then get an MlbLinker instance using this method. Otherwise,
     * since these packages both use bundled, transpiled files, you'll end up with a significant amount of
     * duplicate code (not a problem on the server side; definitely a problem on the client).
     * @return {BamLinker} A BamLinker subclass instance
     */
    getBamLinker() {
        return this._bamLinker;
    }

    /**
     * Returns the BamTeamData subclass instance which backs the BamLinker instance which backs this BamGameUtils
     * instance.  Use this for the same reason you'd use getBamLinker() - to limit the amount of duplicate code
     * downloaded to the client.
     * @return {BamTeamData} A BamTeamData subclass instance
     * @see getBamLinker()
     */
    getBamTeamData() {
        return this._bamLinker.getBamTeamData();
    }

    /**
     * Get a translation from the dictionary
     * @param key
     * @param defaultVal
     * @return {string} The translation if present, or the defaultVal if present, or the key
     * @protected
     */
    _getTrans(key, defaultVal) {
        return (this._dictionaryData[key] || defaultVal || key);
    }


    /**
     * Get the Helpers class
     * @return {Helpers}
     * @protected
     */
    static getHelpers() {
        return Helpers;
    }

    /**
     * Get the BamButton class
     * @return the BamButton class
     * @protected
     */
    static getBamButtonClass() {
        return BamButton;
    }

    /**
     * Get the BamButtonGroup class
     * @return the BamButtonGroup class
     * @protected
     */
    static getBamButtonGroupClass() {
        return BamButtonGroup;
    }

}

export default BamGameUtils;

